import { autorun, observable, reaction, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"
import { isPreviewPortBeingListened } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"

import type { TaskPreviewModel } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { IObservableValue } from "mobx"

const TabId = {
  Logs: "logs",
  Log: (logId: string) => `log:${logId}`,
  Errors: "errors",
  SshCommands: "ssh-commands",
  Status: "status",
}

export class DevToolsPanelModel extends Model {
  readonly currentTab = observable.box<string>("")

  readonly sshConnectionLoading = observable.box(false)
  readonly sshTunnelLoading = observable.box(false)

  readonly keyCount = observable.box(0)

  readonly connectCommand = observable.box<string | null>(null)
  readonly tunnelCommand = observable.box<string | null>(null)

  readonly dialogOpen = observable.map<string, boolean>()

  private sshConnectionFetched = false
  private sshTunnelFetched = false

  readonly getTerminalModels = () => {
    const previewModel = this.taskViewModel?.currentPreview?.get() || null
    return previewModel
      ? [...previewModel.terminals].map(([, v]) => v)
      : []
  }

  readonly getFirstAvailableTab = () => {
    if (this.errors?.get().size ?? 0 > 0) {
      return TabId.Errors
    }

    const terminals = this.getTerminalModels()
    if (terminals.length > 0 && terminals[0].logModel?.id) {
      return TabId.Log(terminals[0].logModel.id)
    }

    return TabId.Errors
  }

  constructor(
    readonly taskViewModel: TaskViewModel | null,
    readonly openLogId: IObservableValue<string | null>,
    readonly errors?: IObservableValue<Map<string, Error>>
  ) {
    super("DevToolsPanelModel")

    runInAction(() => this.currentTab.set(this.getFirstAvailableTab()))

    this.lifetime.add(autorun(() => {
      const logId = openLogId.get()
      const previewModel = taskViewModel?.currentPreview?.get() || null
      if (logId && previewModel?.terminals.has(logId)) {
        this.setCurrentTab(TabId.Log(logId))
      }
    }), "DevToolsPanelModel:openLogId")

    this.lifetime.add(reaction(
      () => this.currentTab.get(),
      (currentTab) => {
        if (currentTab === TabId.SshCommands) {
          void this.fetchSshCommandsIfNeeded()
        }
      }
    ), "DevToolsPanelModel:currentTab")
  }

  readonly setCurrentTab = (tab: string) => {
    runInAction(() => this.currentTab.set(tab))
  }

  readonly setDialogOpen = (logId: string, open: boolean) => {
    runInAction(() => this.dialogOpen.set(logId, open))
  }

  readonly getDialogOpen = (logId: string) => {
    return this.dialogOpen.get(logId) ?? false
  }

  readonly refetchSshCommands = () => {
    this.sshConnectionFetched = false
    this.sshTunnelFetched = false
    void this.fetchSshCommandsIfNeeded()
  }

  private async fetchSshCommandsIfNeeded() {
    const previewModel = this.taskViewModel?.currentPreview?.get() || null
    if (!previewModel) return

    if (!this.sshConnectionFetched && !this.sshConnectionLoading.get()) {
      await this.getSshConnection(previewModel)
    }

    const previewState = previewModel.previewProcessesState.get()
    const canFetchTunnel = previewState?.className === "Running" && isPreviewPortBeingListened(previewState)

    if (canFetchTunnel && !this.sshTunnelFetched && !this.sshTunnelLoading.get()) {
      await this.getSshTunnel(previewModel)
    }
  }

  private async getSshConnection(previewModel: TaskPreviewModel) {
    runInAction(() => this.sshConnectionLoading.set(true))

    try {
      const connect = await previewModel.getSshConnectCommand()
      runInAction(() => {
        this.keyCount.set(connect.keys.length)
        this.connectCommand.set(connect.command)
      })
    } finally {
      runInAction(() => this.sshConnectionLoading.set(false))
      this.sshConnectionFetched = true
    }
  }

  private async getSshTunnel(previewModel: TaskPreviewModel) {
    runInAction(() => this.sshTunnelLoading.set(true))

    try {
      const tunnel = await previewModel.getSshTunnelCommand()
      runInAction(() => {
        this.keyCount.set(tunnel.keys.length)
        this.tunnelCommand.set(tunnel.command)
      })
    } finally {
      runInAction(() => this.sshTunnelLoading.set(false))
      this.sshTunnelFetched = true
    }
  }
}